/**
 * The Charter HUD's model and view (M5 spec §15.4, §18 flows 1, 2 and 6; plan C18).
 *
 * - **Jurisdiction chip** under the system name: "Tethys claim · tariff 6% · toll 150 · WANTED 2", "UNCLAIMED · NO
 *   CLAIM LAW" (no claim: CL-7 wording, still lawless by spec §12.1), or "Lawless" (held by an outlaw). A corporation claim reads "‹Corp› claim · security Medium · tariff
 *   4%" (or "· tariff not set"), tone lawful, never "Lawless" (D-424 D5, spec §27.7; labels in charterHud.json).
 * - **Toasts** (a fixed ring): bounties posted or cleared, clearance refusals with the fines offer, fines paid, gate
 *   stage changes and closures ("Gate tremor: closed for 2 h"), reopening.
 * - **Scan meter** ("Being scanned"), the **zone marker and prompt** within the HUD range, the **Isolated Era title
 *   card**, and the **resonance meter** (resonance.ts; tier-gated).
 *
 * Strings are built at message rate (a new view, relations, a gate change, a refusal), never per frame. The view
 * (`createCharterHudView`) writes only `transform` and `textContent` (and a `data-*` tone when it changes) on cached
 * elements, and nothing at all when the state is unchanged (the M1 HUD rule, D-014).
 */
import { z } from 'zod'
import { playerToll, factionShort } from '../../charter/tolls'
import { FACTION_OUTLAW } from '../../charter/hostilityWriter'
import { DOCKING_POLICY_REASON } from '../../docking/clearance'
import {
  FACTION,
  FACTION_IDS,
  PLAYER_FACTION_ID,
  type ClaimCorpView,
  type ClaimView,
  type CorpFactionId,
  type FactionId,
  type GateView,
  type RelationsView,
  type SecurityBand,
} from '../../strategic/types'
import { createResonance, meterBand, trendSample, trendText, type ResonanceState } from './resonance'
import charterHudJson from './charterHud.json'
import { hudStatus, statusHide, statusShow, STATUS_HIDDEN, type StatusStack } from '../../hud/status'

// ---------------------------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------------------------

export const charterHudSchema = z.strictObject({
  comment: z.string(),
  toasts: z.strictObject({ showS: z.number().positive(), max: z.number().int().min(1).max(16) }),
  finesConfirmS: z.number().positive(),
  isolatedCardS: z.number().positive(),
  meter: z.strictObject({ marks: z.array(z.number().min(0).max(100)).min(1).max(4), trendDays: z.number().int().min(2).max(30) }),
  zoneDistStepKm: z.number().positive(),
  unclaimed: z.strictObject({ label: z.string().min(1), note: z.string().min(1) }),
  corpClaim: z.strictObject({
    claim: z.string().min(1),
    chip: z.string().min(1),
    banner: z.string().min(1),
    tariff: z.string().min(1),
    tariffNotSet: z.string().min(1),
    notSet: z.string().min(1),
    fallbackName: z.string().min(1),
    bands: z.strictObject({ low: z.string().min(1), medium: z.string().min(1), high: z.string().min(1) }),
  }),
})
export type CharterHudConfig = z.infer<typeof charterHudSchema>

export function loadCharterHudConfig(raw: unknown = charterHudJson): CharterHudConfig {
  return charterHudSchema.parse(raw)
}

// ---------------------------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------------------------

export function formatCredits(n: number): string {
  return Math.round(n).toLocaleString('en-US')
}

/** The corporation-claim labels (charterHud.json `corpClaim`; D-424 D5). */
export type CorpClaimLabels = CharterHudConfig['corpClaim']
export const CORP_CLAIM_LABELS: CorpClaimLabels = loadCharterHudConfig().corpClaim

/**
 * CL-7 (D-1158 Q3): what an unclaimed system reads (charterHud.json `unclaimed`): the label on the HUD chip, the
 * station owner line and the Law layer, and the plain line under it. Wording only: unclaimed is still lawless (D-160).
 */
export type UnclaimedLabels = CharterHudConfig['unclaimed']
export const UNCLAIMED_LABELS: UnclaimedLabels = loadCharterHudConfig().unclaimed

/** Fills `{key}` placeholders in a label template. */
export function fillLabel(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g,(m: string, k: string) => values[k] ?? m)
}

/** The security band's name ("Low", "Medium", "High"). */
export function bandLabel(band: SecurityBand, labels: CorpClaimLabels = CORP_CLAIM_LABELS): string {
  return labels.bands[band]
}

/** "tariff 4%" for a manual tariff (a fraction), "tariff not set" for an automatic one (it charges 0 %: spec §27.6). */
export function corpTariffText(tariffSet: boolean, tariff: number, labels: CorpClaimLabels = CORP_CLAIM_LABELS): string {
  return tariffSet ? fillLabel(labels.tariff, { pct: String(Math.round(tariff * 100)) }) : labels.tariffNotSet
}

/** The corporation's name, or the fallback while the view doesn't carry it. */
export function corpClaimName(corp: Pick<ClaimCorpView, 'name'> | null | undefined, labels: CorpClaimLabels = CORP_CLAIM_LABELS): string {
  return corp !== null && corp !== undefined && corp.name !== '' ? corp.name : labels.fallbackName
}

/** "Halcyon claim · security Medium · tariff 4%" (or "· tariff not set"); "‹Corp› claim" without the corp block. */
export function corpChipText(corp: ClaimCorpView | null | undefined, tariff: number, labels: CorpClaimLabels = CORP_CLAIM_LABELS): string {
  const name = corpClaimName(corp, labels)
  if (corp === null || corp === undefined) return fillLabel(labels.claim, { name })
  return fillLabel(labels.chip, { name, band: bandLabel(corp.band, labels), tariff: corpTariffText(corp.tariffSet, tariff, labels) })
}

function shortName(id: FactionId): string {
  return factionShort(id)
}

/** The player's wanted level with `faction` (0 without relations). */
export function wantedWith(relations: RelationsView | null, faction: FactionId): number {
  return relations?.player.wanted[faction] ?? 0
}

/** Whether `holder` is a jurisdiction (a non-outlaw AI faction; the corporation, `'player'`, never is: D-424). */
export function isJurisdiction(holder: CorpFactionId | null): holder is FactionId {
  return holder !== null && holder !== PLAYER_FACTION_ID && FACTION_OUTLAW[FACTION[holder]] !== 1
}

/** The chip's tone: lawful (claimed), lawless, wanted (level ≥ 1 with the jurisdiction). */
export type ChipTone = 'lawful' | 'lawless' | 'wanted'

/** What the chip reads: the claim's holder, tariff and toll, and the corporation block (D-424) when it holds. */
export type ChipClaim = Pick<ClaimView, 'holder' | 'tariff' | 'toll'> & { corp?: ClaimCorpView | null }

/**
 * "Tethys claim · tariff 6% · toll 150 · WANTED 2", "‹Corp› claim · security Medium · tariff not set" (a corporation
 * claim: never a jurisdiction, never "Lawless", no toll and no wanted level), "UNCLAIMED · NO CLAIM LAW" (no claim, or
 * not known yet; CL-7), or "Lawless" (an outlaw holder).
 */
export function jurisdictionChipText(claim: ChipClaim | null, relations: RelationsView | null): string {
  const holder = claim?.holder ?? null
  if (claim !== null && holder === PLAYER_FACTION_ID) return corpChipText(claim.corp, claim.tariff)
  if (claim === null || holder === null) return UNCLAIMED_LABELS.label
  if (!isJurisdiction(holder)) return 'Lawless'
  let s = `${shortName(holder)} claim`
  const pct = Math.round(claim.tariff * 100)
  if (pct > 0) s += ` · tariff ${pct}%`
  const toll = playerToll(claim.toll)
  if (toll > 0) s += ` · toll ${formatCredits(toll)}`
  const w = wantedWith(relations, holder)
  if (w > 0) s += ` · WANTED ${w}`
  return s
}

export function jurisdictionTone(claim: Pick<ClaimView, 'holder'> | null, relations: RelationsView | null): ChipTone {
  const holder = claim?.holder ?? null
  if (holder === PLAYER_FACTION_ID) return 'lawful'
  if (claim === null || !isJurisdiction(holder)) return 'lawless'
  return wantedWith(relations, holder) > 0 ? 'wanted' : 'lawful'
}

/** The gate's plain status before tier 3 (spec §10): "Gate operating", "Transit delays", "Tremors", "Closed". */
export function gateStatusText(g: GateView): string {
  if (g.isolation || g.stage === 'closed') return 'Closed'
  if (g.stage === 'tremors') return 'Tremors'
  if (g.stage === 'flickers') return 'Transit delays'
  return 'Gate operating'
}

/** Whole hours left until `until` (at least 1). */
export function hoursLeft(until: number, now: number): number {
  return Math.max(1, Math.ceil((until - now) / 3600))
}

/**
 * The toast for a gate change (null = none): stage changes, a closure ("Gate tremor: closed for 2 h"), a closure's
 * end, reopening after the Isolated Era. The Isolated Era itself is the title card (`enteredIsolation`). Never names
 * tetherstone (spec §15.1).
 */
export function gateToastText(prev: GateView | null, next: GateView, now: number): string | null {
  if (prev === null) return null
  if (prev.isolation && !next.isolation) return 'Gate reopened: transit resumes'
  if (next.isolation) return null
  if (prev.closedUntil === null && next.closedUntil !== null) return `Gate tremor: closed for ${hoursLeft(next.closedUntil, now)} h`
  if (prev.closedUntil !== null && next.closedUntil === null) return 'Gate open again after the tremor'
  if (prev.stage === next.stage) return null
  if (next.stage === 'flickers') return 'Gate transit delays'
  if (next.stage === 'tremors') return 'Gate tremors: closures possible'
  if (next.stage === 'normal') return 'Gate operating normally'
  return null
}

/** True when the gate just closed for good (the Isolated Era title card). */
export function enteredIsolation(prev: GateView | null, next: GateView): boolean {
  return next.isolation && (prev === null ? false : !prev.isolation)
}

/** The refusal line of flow 1: "Clearance denied: wanted by Tethys (level 1)". */
export function clearanceRefusalText(reason: number, faction: FactionId | null, level: number): string {
  const who = faction === null ? 'the station' : shortName(faction)
  if (reason === DOCKING_POLICY_REASON.hostileStanding) return `Clearance denied: your standing with ${who} is too low`
  return `Clearance denied: wanted by ${who} (level ${Math.max(1, level)})`
}

/** The fines line of flow 1: at an Authority station in comms range "Pay fines now: …", else where to pay. */
export function finesOfferText(total: number, here: boolean): string {
  if (here) return `Pay fines now: ${formatCredits(total)}. Request docking again to pay.`
  return 'Pay fines at an Authority station'
}

// ---------------------------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------------------------

export type ToastKind = 'info' | 'warn' | 'alert'

export interface Toast {
  text: string
  kind: ToastKind
}

export interface ToastQueue {
  /** Waiting toasts (a ring). */
  readonly slots: Toast[]
  /** The record on screen (`current` points here while a toast shows). */
  readonly shown: Toast
  head: number
  count: number
  /** The toast on screen (`shown`), or null. */
  current: Toast | null
  shownS: number
  /** Bumps whenever `current` changes. */
  version: number
}

export function createToastQueue(max: number): ToastQueue {
  const slots: Toast[] = []
  for (let i = 0; i < max; i++) slots.push({ text: '', kind: 'info' })
  return { slots, shown: { text: '', kind: 'info' }, head: 0, count: 0, current: null, shownS: 0, version: 0 }
}

/** Queues a toast (message rate). With the queue full the oldest waiting toast drops. */
export function pushToast(q: ToastQueue, text: string, kind: ToastKind = 'info'): void {
  const cap = q.slots.length
  if (q.count === cap) {
    q.head = (q.head + 1) % q.slots.length
    q.count--
  }
  const s = q.slots[(q.head + q.count) % q.slots.length]
  s.text = text
  s.kind = kind
  q.count++
  if (q.current === null) nextToast(q)
}

function nextToast(q: ToastQueue): void {
  if (q.count === 0) {
    q.current = null
  } else {
    const next = q.slots[q.head]
    q.shown.text = next.text
    q.shown.kind = next.kind
    q.current = q.shown
    q.head = (q.head + 1) % q.slots.length
    q.count--
  }
  q.shownS = 0
  q.version++
}

/** Advances the toast clock (allocation-free). */
export function stepToasts(q: ToastQueue, dt: number, showS: number): void {
  if (q.current === null) return
  q.shownS += dt
  if (q.shownS >= showS) nextToast(q)
}

export function clearToasts(q: ToastQueue): void {
  q.count = 0
  if (q.current !== null) q.version++
  q.current = null
  q.shownS = 0
}

/**
 * Bounty toasts from a relations change (message rate): a rising bounty ("Tethys bounty 17,000 · WANTED 1"), and
 * "Bounties cleared" when every bounty went to zero. `last` holds the previous bounties per faction index (updated).
 * The first view (`primed` false) only records.
 */
export function bountyToasts(q: ToastQueue, last: Float64Array, view: RelationsView, primed: boolean): void {
  let had = false
  let has = false
  for (let f = 0; f < FACTION_IDS.length; f++) {
    const id = FACTION_IDS[f]
    const b = view.player.bounties[id] ?? 0
    if (last[f] > 0) had = true
    if (b > 0) has = true
    if (primed && b > last[f] && FACTION_OUTLAW[f] !== 1) {
      const w = view.player.wanted[id] ?? 0
      pushToast(q, `${shortName(id)} bounty ${formatCredits(b)}${w > 0 ? ` · WANTED ${w}` : ''}`, w >= 2 ? 'alert' : 'warn')
    }
    last[f] = b
  }
  if (primed && had && !has) pushToast(q, 'Bounties cleared', 'info')
}

// ---------------------------------------------------------------------------------------------
// Zones (a pooled table from the system view)
// ---------------------------------------------------------------------------------------------

export const HUD_ZONE_CAPACITY = 8

export interface HudZones {
  count: number
  readonly x: Float64Array
  readonly y: Float64Array
  readonly z: Float64Array
  readonly prompt: string[]
}

export function createHudZones(): HudZones {
  const prompt: string[] = []
  for (let i = 0; i < HUD_ZONE_CAPACITY; i++) prompt.push('')
  return { count: 0, x: new Float64Array(HUD_ZONE_CAPACITY), y: new Float64Array(HUD_ZONE_CAPACITY), z: new Float64Array(HUD_ZONE_CAPACITY), prompt }
}

/** A zone as the view carries it. */
export interface HudZoneInput {
  sideA: FactionId
  sideB: FactionId
  node: { pos: readonly [number, number, number] | [number, number, number] }
}

/** Refills the table (message rate) with the zones and their prompts (`prompt(a, b)`). */
export function fillHudZones(zt: HudZones, list: readonly HudZoneInput[], prompt: (a: FactionId, b: FactionId) => string): void {
  const n = Math.min(HUD_ZONE_CAPACITY, list.length)
  for (let i = 0; i < n; i++) {
    const zv = list[i]
    zt.x[i] = zv.node.pos[0]
    zt.y[i] = zv.node.pos[1]
    zt.z[i] = zv.node.pos[2]
    zt.prompt[i] = prompt(zv.sideA, zv.sideB)
  }
  zt.count = n
}

/** The nearest zone within `rangeM` of the point, or −1; its distance goes to `out[0]`. Allocation-free. */
export function nearestHudZone(zt: HudZones, px: number, py: number, pz: number, rangeM: number, out: Float64Array): number {
  let best = -1
  let bestD2 = rangeM * rangeM
  for (let i = 0; i < zt.count; i++) {
    const dx = zt.x[i] - px
    const dy = zt.y[i] - py
    const dz = zt.z[i] - pz
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 <= bestD2) {
      bestD2 = d2
      best = i
    }
  }
  out[0] = best < 0 ? 0 : Math.sqrt(bestD2)
  return best
}

// ---------------------------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------------------------

export interface CharterHudState {
  active: boolean
  chip: string
  chipTone: ChipTone
  readonly toasts: ToastQueue
  scanActive: boolean
  /** 0..1. */
  scanProgress: number
  /** The zone marker: its prompt ('' = none), distance (m), screen point. */
  zonePrompt: string
  zoneDistM: number
  zoneOnScreen: boolean
  zoneX: number
  zoneY: number
  /** Seconds left on the Isolated Era title card (0 = hidden). */
  cardS: number
  /** The persistent gate status line (before tier 3; '' when normal). */
  gateStatus: string
  meterVisible: boolean
  readonly resonance: ResonanceState
  /** Seconds left in which a second policy refusal pays the fines (flow 2's confirm). */
  finesConfirmS: number
}

export function createCharterHudState(cfg: CharterHudConfig): CharterHudState {
  return {
    active: false,
    chip: UNCLAIMED_LABELS.label,
    chipTone: 'lawless',
    toasts: createToastQueue(cfg.toasts.max),
    scanActive: false,
    scanProgress: 0,
    zonePrompt: '',
    zoneDistM: 0,
    zoneOnScreen: false,
    zoneX: 0,
    zoneY: 0,
    cardS: 0,
    gateStatus: '',
    meterVisible: false,
    resonance: createResonance(cfg.meter.trendDays),
    finesConfirmS: 0,
  }
}

/** Per-frame clocks (allocation-free). */
export function stepCharterHud(s: CharterHudState, dt: number, cfg: CharterHudConfig): void {
  stepToasts(s.toasts, dt, cfg.toasts.showS)
  if (s.cardS > 0) s.cardS = Math.max(0, s.cardS - dt)
  if (s.finesConfirmS > 0) s.finesConfirmS = Math.max(0, s.finesConfirmS - dt)
}

// ---------------------------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------------------------

interface Styled {
  style: { transform: string }
}
interface Texted {
  textContent: string | null
}
interface Toned extends Styled {
  dataset: Record<string, string | undefined>
}

/** The elements the view writes (real DOM in the game, fakes in tests). */
export interface CharterHudElements {
  root: Toned
  chip: Toned
  chipText: Texted
  toast: Toned
  toastText: Texted
  scan: Styled
  scanBar: Styled
  zone: Styled
  zonePrompt: Texted
  zoneDist: Texted
  card: Styled
  gate: Toned
  gateText: Texted
  meter: Toned
  meterFill: Styled
  meterValue: Texted
  meterTrend: Texted
  meterBars: Styled[]
}

export interface CharterHudView {
  /** Writes the state; writes nothing when nothing changed. */
  sync(s: CharterHudState): void
}

const HIDDEN = 'scale(0)'
const SHOWN = 'none'

/** Shows or hides a status-stack chip (H11) when its wanted state changes; returns the new state. Event-driven. */
function stackChip(stack: StatusStack, n: Toned, was: boolean, on: boolean): boolean {
  if (on !== was) {
    if (on) statusShow(stack, n)
    else statusHide(stack, n)
  }
  return on
}

/**
 * The chip, the gate line and the toast are chips of the top-centre status stack (H11, D-617 §6; `hud/status.ts`):
 * the stack writes their slot transforms, newest on top; everything else is placed here.
 */
export function createCharterHudView(els: CharterHudElements, cfg: CharterHudConfig, zoneRangeM: number, stack: StatusStack = hudStatus): CharterHudView {
  // Precomputed strings (D-014): 0..100 scales and values, zone distances in steps.
  const scaleX: string[] = []
  const scaleY: string[] = []
  const values: string[] = []
  for (let i = 0; i <= 100; i++) {
    scaleX.push(`scaleX(${i / 100})`)
    scaleY.push(`scaleY(${i / 100})`)
    values.push(String(i))
  }
  const stepM = cfg.zoneDistStepKm * 1000
  const distSteps = Math.ceil(zoneRangeM / stepM)
  const dists: string[] = []
  const decimals = cfg.zoneDistStepKm < 1 ? 1 : 0
  for (let i = 0; i <= distSteps; i++) dists.push(`${((i * stepM) / 1000).toFixed(decimals)} km`)

  for (const n of [els.chip, els.toast, els.gate]) {
    n.style.transform = STATUS_HIDDEN
    n.dataset.on = '0'
  }
  let chipOn = false
  let toastOn = false
  let gateOn = false
  let active: boolean | null = null
  let chip = ''
  let chipTone = ''
  let toastVersion = -1
  let scanShown: boolean | null = null
  let scanPct = -1
  let zonePrompt: string | null = null
  let zoneDist = -1
  let zonePx = Number.NaN
  let zonePy = Number.NaN
  let zoneShown: boolean | null = null
  let cardShown: boolean | null = null
  let gateText: string | null = null
  let meterShown: boolean | null = null
  let meterPct = -2
  let meterBandShown = -1
  let trendVersion = -1

  return {
    sync(s: CharterHudState): void {
      if (s.active !== active) {
        active = s.active
        els.root.style.transform = s.active ? SHOWN : HIDDEN
      }
      if (s.chip !== chip) {
        chip = s.chip
        els.chipText.textContent = s.chip
      }
      // The chip mounts hidden like the rest and shows once it has text (it never showed before corp-law CL7).
      chipOn = stackChip(stack, els.chip, chipOn, s.active && s.chip !== '')
      if (s.chipTone !== chipTone) {
        chipTone = s.chipTone
        els.chip.dataset.tone = s.chipTone
      }
      const t = s.toasts
      if (t.version !== toastVersion) {
        toastVersion = t.version
        if (t.current !== null) {
          els.toastText.textContent = t.current.text
          els.toast.dataset.kind = t.current.kind
          // A new toast replacing a shown one is the newest chip again.
          if (toastOn && s.active) statusShow(stack, els.toast)
        }
      }
      toastOn = stackChip(stack, els.toast, toastOn, s.active && t.current !== null)
      if (s.scanActive !== scanShown) {
        scanShown = s.scanActive
        els.scan.style.transform = s.scanActive ? SHOWN : HIDDEN
      }
      const pct = Math.round(Math.min(1, Math.max(0, s.scanProgress)) * 100)
      if (s.scanActive && pct !== scanPct) {
        scanPct = pct
        els.scanBar.style.transform = scaleX[pct]
      }
      const hasZone = s.zonePrompt !== ''
      if (s.zonePrompt !== zonePrompt) {
        zonePrompt = s.zonePrompt
        els.zonePrompt.textContent = s.zonePrompt
      }
      if (hasZone) {
        const d = Math.min(distSteps, Math.max(0, Math.round(s.zoneDistM / stepM)))
        if (d !== zoneDist) {
          zoneDist = d
          els.zoneDist.textContent = dists[d]
        }
      }
      const zoneVisible = hasZone && s.zoneOnScreen
      if (zoneVisible) {
        const px = Math.round(s.zoneX)
        const py = Math.round(s.zoneY)
        if (px !== zonePx || py !== zonePy || zoneShown !== true) {
          zonePx = px
          zonePy = py
          // A moving marker needs a new transform string (the one unavoidable HUD string, as in M3's markers).
          els.zone.style.transform = `translate(${px}px, ${py}px)`
        }
      } else if (zoneShown !== false) {
        els.zone.style.transform = HIDDEN
      }
      zoneShown = zoneVisible
      const card = s.cardS > 0
      if (card !== cardShown) {
        cardShown = card
        els.card.style.transform = card ? SHOWN : HIDDEN
      }
      if (s.gateStatus !== gateText) {
        gateText = s.gateStatus
        els.gateText.textContent = s.gateStatus
      }
      gateOn = stackChip(stack, els.gate, gateOn, s.active && s.gateStatus !== '')
      if (s.meterVisible !== meterShown) {
        meterShown = s.meterVisible
        els.meter.style.transform = s.meterVisible ? SHOWN : HIDDEN
      }
      if (s.meterVisible) {
        const r = s.resonance
        const v = Number.isNaN(r.value) ? -1 : Math.round(r.value)
        if (v !== meterPct) {
          meterPct = v
          els.meterFill.style.transform = scaleY[v < 0 ? 0 : v]
          els.meterValue.textContent = v < 0 ? '—' : values[v]
        }
        const band = meterBand(r.value, cfg.meter.marks)
        if (band !== meterBandShown) {
          meterBandShown = band
          els.meter.dataset.band = values[band]
        }
        if (r.version !== trendVersion) {
          trendVersion = r.version
          els.meterTrend.textContent = trendText(r)
          const bars = els.meterBars
          for (let i = 0; i < bars.length; i++) {
            const k = i - (bars.length - r.count)
            const sv = k < 0 ? 0 : Math.round(trendSample(r, k))
            bars[i].style.transform = scaleY[sv < 0 ? 0 : sv > 100 ? 100 : sv]
          }
        }
      }
    },
  }
}
